import type { Pieces } from 'components/geometry-editing';
import { mergeOuterRings, piecesToGeojson } from './compose-boundary';

/** The boundary reduced to the one polygon a unit is allowed to be. */
export interface UnitPolygon {
  /** Separate shapes left AFTER merging — rings of at least three points. */
  ringCount: number;
  /** The polygon to send. Null unless the merge leaves exactly one ring. */
  polygon: GeoJSON.Polygon | null;
}

/**
 * The single polygon to save, and how many shapes the boundary really is.
 *
 * Merges first, so pieces that share an edge dissolve into the one outline they
 * visually are — three touching polygons save as one polygon. Only genuinely
 * disjoint pieces survive `mergeOuterRings`, and those are what make the
 * boundary unsaveable: a unit is a polygon, never a multipolygon.
 *
 * The merge is re-run here rather than trusted from the composition, because a
 * freehand edit bypasses it. `composeUnitBoundary` unions its parts, but dragging
 * two pieces until they touch leaves `shapeEdit.pieces` holding two rings — so
 * judging the shape as STORED would refuse a boundary that is, as drawn, one
 * polygon. Union of a single ring returns it unchanged, so this is a no-op on
 * pieces that are already merged.
 *
 * `mergeOuterRings` is deliberately fail-soft: a pair turf cannot union keeps the
 * accumulator, so a brittle shape stays split and is reported as unsaveable
 * rather than silently collapsing to one of its halves.
 *
 * @param {Pieces} pieces - The working boundary's rings.
 * @return {UnitPolygon} The merged ring count, and the polygon when there is one.
 */
export const unitPolygonFrom = (pieces: Pieces): UnitPolygon => {
  const geojson = piecesToGeojson(mergeOuterRings(pieces));
  if (!geojson) return { ringCount: 0, polygon: null };
  if (geojson.type === 'Polygon') return { ringCount: 1, polygon: geojson };
  return { ringCount: geojson.coordinates.length, polygon: null };
};
